import React, { useState, useMemo } from 'react'
import { Table, DatePicker, Button } from 'antd'
import styled from '@emotion/styled'
import moment from 'moment'
import { useQuery } from '@apollo/client'
import { GET_REF_DETAIL } from 'Queries'

const dateFormat = 'YYYY/MM/DD'

export const FamiliesDetailBySegmentation = ({
  codDistribuidor,
  codObjetivo,
  year,
  fechaDesde,
  fechaHasta,
  isMensual
}) => {
  const objectiveStart = moment(fechaDesde, 'x').utc().startOf('day')
  const objectiveEnd = moment(fechaHasta, 'x').utc().endOf('day')

  // Ajuste: si hoy cae dentro del objetivo, filtrar desde día 1 de este mes hasta hoy
  const today = moment().utc()
  const todayInObjective = isMensual? today.isSameOrAfter(objectiveStart) && today.isSameOrBefore(objectiveEnd) : false;
  
  const effectiveStart = todayInObjective ? today.clone().startOf('month') : objectiveStart
  const effectiveEnd = todayInObjective ? today : objectiveEnd

  // Estados iniciales
  const initialPeriod = [effectiveStart, effectiveEnd]
  const [period, setPeriod] = useState(initialPeriod)

  // Carga de datos del detalle por segmentación
  const { data, loading, error } = useQuery(GET_REF_DETAIL, {
    variables: {
      codDistribuidor,
      codObjetivo,
      ano: year,
      fromDate: period[0]?.format('YYYY-MM-DD'),
      toDate: period[1]?.format('YYYY-MM-DD')
    }
  })

  // Función para resetear filtros
  const handleResetFilters = () => {
    setPeriod(initialPeriod)
  }

  // Verificar si hay filtros activos
  const hasActiveFilters = period && (
    period[0]?.valueOf() !== initialPeriod[0]?.valueOf() ||
    period[1]?.valueOf() !== initialPeriod[1]?.valueOf()
  )

  // Generar datos de la tabla principal (segmentaciones + total)
  const tableData = useMemo(() => {
    const rows = (data?.refDetail?.segmentationData || []).map((item, index) => ({
      key: `seg-${index}`,
      segmentacion: item.segmentacion,
      numClientes: item.numClientes,
      numClientesAA: item.numClientesAA,
      isData: true,
      isTotal: false
    }))

    // Usar totales del API o calcular si no están disponibles
    const totalAcumMes = data?.refDetail?.totalAcumMesActual || rows.reduce((sum, row) => sum + row.numClientes, 0)
    const totalAcumMesAA = data?.refDetail?.totalAcumMesAA || rows.reduce((sum, row) => sum + row.numClientesAA, 0)

    // Agregar fila de totales
    rows.push({
      key: 'total',
      segmentacion: 'Total acum. mes',
      numClientes: totalAcumMes,
      numClientesAA: totalAcumMesAA,
      isData: false,
      isTotal: true
    })

    return rows
  }, [data?.refDetail?.segmentationData, data?.refDetail?.totalAcumMesActual, data?.refDetail?.totalAcumMesAA])

  // Generar datos de la tabla de resumen (faltantes + objetivo)
  const summaryTableData = useMemo(() => {
    const faltantes = data?.refDetail?.faltantes || 0
    const numTotalObjetivo = data?.refDetail?.numTotalObjetivo || 0

    return [
      {
        key: 'faltantes',
        label: 'Faltantes',
        valor: faltantes,
      },
      {
        key: 'objetivo',
        label: 'Num. Total Objetivo',
        valor: numTotalObjetivo,
      }
    ]
  }, [data?.refDetail?.faltantes, data?.refDetail?.numTotalObjetivo])

  // Función de exportación a CSV
  const handleExport = () => {
    const headers = [
      'Segmentaciones',
      'Nº clientes',
      'Nº clientes AA'
    ]

    const csvContent = [
      headers.join(';'),
      ...tableData.map(row => {
        if (row.isFaltantes || row.isObjetivo) {
          return [row.segmentacion, row.numClientes, ''].join(';')
        }
        return [
          row.segmentacion,
          row.numClientes,
          row.numClientesAA
        ].join(';')
      })
    ].join('\n')

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `familias_detalle_segmentacion_${moment().format('YYYYMMDD')}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // Definición de columnas para la tabla principal
  const columns = [
    {
      title: 'Segmentaciones',
      dataIndex: 'segmentacion',
      key: 'segmentacion',
      width: 200,
      align: 'center',
      ellipsis: {
        showTitle: false,
      },
      render: (value, record) => ({
        children: (
          <span
            style={{
              fontWeight: record.isTotal ? 'bold' : 'normal',
              display: 'block',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
            title={value}
          >
            {value}
          </span>
        ),
        props: {
          style: {
            background: record.isData ? '#D4EFDF' : record.isTotal ? '#E8E8E8' : '#FFFFFF',
            fontWeight: record.isTotal ? 'bold' : 'normal'
          }
        },
      }),
    },
    {
      title: 'Nº clientes',
      dataIndex: 'numClientes',
      key: 'numClientes',
      width: 150,
      align: 'center',
      render: (value, record) => ({
        children: <span style={{ fontWeight: record.isTotal ? 'bold' : 'normal' }}>{value}</span>,
        props: {
          style: {
            background: record.isData ? '#F3FAF3' : '#FFFFFF',
            fontWeight: record.isTotal ? 'bold' : 'normal'
          }
        },
      }),
    },
    {
      title: 'Nº clientes AA',
      dataIndex: 'numClientesAA',
      key: 'numClientesAA',
      width: 150,
      align: 'center',
      render: (value, record) => ({
        children: <span style={{ fontWeight: record.isTotal ? 'bold' : 'normal' }}>{value}</span>,
        props: {
          style: {
            background: record.isData ? '#F3FAF3' : '#FFFFFF',
            fontWeight: record.isTotal ? 'bold' : 'normal'
          }
        },
      }),
    },
  ]

  // Definición de columnas para la tabla de resumen
  const summaryColumns = [
    {
      dataIndex: 'label',
      key: 'label',
      width: 200,
      align: 'center',
      render: (value) => ({
        children: <span style={{ fontWeight: 'bold' }}>{value}</span>,
        props: {
          style: {
            background: '#D3D3D3',
            fontWeight: 'bold'
          }
        },
      }),
    },
    {
      dataIndex: 'valor',
      key: 'valor',
      width: 150,
      align: 'center',
      render: (value) => ({
        children: <span style={{ fontWeight: 'bold' }}>{value}</span>,
        props: {
          style: {
            background: '#E8E8E8',
            fontWeight: 'bold'
          }
        },
      }),
    },
  ]

  return (
    <Container>
      <ControlsWrapper>
        <LeftControls>
          <Button
            type="primary"
            onClick={handleExport}
            size="large"
          >
            Descargar CSV
          </Button>
        </LeftControls>
        <RightControls>
          <DatePicker.RangePicker
            allowClear={false}
            disabledDate={current => {
              return current && (current < objectiveStart || objectiveEnd < current)
            }}
            onChange={selection => {
              if (selection && selection.length > 1) {
                const [start, end] = selection
                // Si la fecha final es menor que la inicial, invertir
                setPeriod(start <= end ? selection : [end, start])
              }
            }}
            size="small"
            value={period}
            format={dateFormat}
            style={{ width: '240px' }}
          />
          <Button
            disabled={!hasActiveFilters}
            type="link"
            size="large"
            onClick={handleResetFilters}
          >
            Resetear filtros
          </Button>
        </RightControls>
      </ControlsWrapper>

      {/* Tabla Principal: Detalle por Segmentación */}
      <TableWrapper>
        <StyledTable
          dataSource={tableData}
          columns={columns}
          loading={loading}
          pagination={false}
          locale={{ emptyText: 'No hay datos disponibles' }}
        />
      </TableWrapper>

      {/* Tabla de Resumen: Faltantes y Objetivo */}
      <SummaryTableWrapper>
        <StyledTable
          dataSource={summaryTableData}
          columns={summaryColumns}
          pagination={false}
          showHeader={false}
          tableLayout="fixed"
        />
      </SummaryTableWrapper>
    </Container>
  )
}

const Container = styled.div`
  padding: 0;
`

const ControlsWrapper = styled.div`
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-width: 100%;
  gap: 10px;
`

const LeftControls = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`

const RightControls = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-shrink: 0;
  > * {
    margin: 0 5px;
  }
`

const TableWrapper = styled.div`
  background: transparent;
  box-shadow: none;
  margin-bottom: 0px;
  
  & .ant-table {
    background: white;
    box-shadow: 0 4px 12px 0 rgba(200, 198, 198, 0.5);
  }
`

const SummaryTableWrapper = styled.div`
  background: transparent;
  box-shadow: none;
  margin-top: 0px;
  width: 70%;
  
  & .ant-table {
    background: white;
    box-shadow: 0 4px 12px 0 rgba(200, 198, 198, 0.5);
  }
`

const StyledTable = styled(Table)`
  & .ant-table-thead > tr > th {
    font-size: 14px;
    font-weight: 700;
    background: #FFFFFF !important;
    color: #205527 !important;
    padding: 8px 8px !important;
    text-align: center;
  }
  & .ant-table-tbody > tr > td {
    border-top: 1px solid darkGray;
    border-bottom: none;
  }
`
